import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../core/breadcrumbs";
import { apiClient } from "../../services/apiClient";
import { useToast } from "../../Toaster/ToastContext";

const SupplierReport = () => {
  const { showToast } = useToast();
  const [supplierData, setSupplierData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch supplier report
  const fetchSupplierReport = async () => {
    try {
      const response = await apiClient.post(`/generateSupplierReport`, {
        type: "preview",
      });
      if (response?.success && Array.isArray(response.data)) {
        setSupplierData(response.data);
      } else {
        setSupplierData([]);
      }
    } catch (error) {
      console.error("Failed to fetch supplier report:", error);
      showToast("Failed to fetch supplier report", "error");
    }
  };

  useEffect(() => {
    fetchSupplierReport();
  }, []);

  // Handle Excel export
  const handleExportExcel = async () => {
    try {
      const response = await apiClient.post(`/generateSupplierReport`, {});
      const fileUrl = response?.path;

      if (fileUrl) {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = "supplier-report.xlsx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Supplier report exported to Excel successfully", "success");
      } else {
        showToast("No file URL found in response", "error");
      }
    } catch (error) {
      showToast("Failed to export supplier report to Excel", "error");
    }
  };

  const filteredData = supplierData.filter((item) => {
    const term = searchTerm.toLowerCase();
    return (
      item.product?.toLowerCase().includes(term) ||
      item.supplier_name?.toLowerCase().includes(term) ||
      (item.amount &&
        parseFloat(item.amount)
          .toFixed(2)
          .toString()
          .toLowerCase()
          .includes(term))
    );
  });

  const renderPurchaseRows = () => {
    return filteredData.map((item) => (
      <tr key={item.id}>
        <td>
          <span className="text-wrap">{item.product || "N/A"}</span>
        </td>
        <td>
          <span className="text-wrap">{item.supplier_name || "N/A"}</span>
        </td>
        <td>
          <span className="text-wrap">
            {item.amount ? `PKR ${parseFloat(item.amount).toFixed(2)}` : "N/A"}
          </span>
        </td>
        <td>
          <span className="text-wrap">{item.purchase_qty || "N/A"}</span>
        </td>
        <td>
          <span className="text-wrap">{item.paid || 0}</span>
        </td>
        <td>
          <span className="badges status-badge">{item.status || "N/A"}</span>
        </td>
      </tr>
    ));
  };

  // Render payment rows
  const renderPaymentRows = () => {
    return supplierData.map((item) => (
      <tr key={item.id}>
        <td>
          {item.amount ? `PKR ${parseFloat(item.amount).toFixed(2)}` : "N/A"}
        </td>
        <td>{item.reference || "N/A"}</td>
        <td>{item.supplier_name || "N/A"}</td>
        <td>
          {item.amount ? `PKR ${parseFloat(item.amount).toFixed(2)}` : "N/A"}
        </td>
        <td>{item.payment_method || "N/A"}</td>
      </tr>
    ));
  };

  // Render return rows
  const renderReturnRows = () => {
    return supplierData.map((item) => (
      <tr key={item.id}>
        <td>{item.reference || "N/A"}</td>
        <td>{item.supplier_name || "N/A"}</td>
        <td>
          {item.amount ? `PKR ${parseFloat(item.amount).toFixed(2)}` : "N/A"}
        </td>
        <td>{"N/A"}</td> {/* No paid in response */}
        <td>{"N/A"}</td> {/* No due_amount in response */}
        <td>
          <span className="badges status-badge">{item.status || "N/A"}</span>
        </td>
        <td>
          <span className="badge-lineunknown">{"N/A"}</span>{" "}
          {/* No payment_status in response */}
        </td>
      </tr>
    ));
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Breadcrumbs
          maintitle="Supplier Report"
          subtitle="Manage Your Supplier Report"
          onExcelClick={handleExportExcel}
        />
        <div className="table-tab">
          <div className="tab-content" id="pills-tabContent">
            {/* Purchase Tab */}
            <div
              className="tab-pane fade show active"
              id="purchase-report"
              role="tabpanel"
              aria-labelledby="purchase-report-tab"
            >
              <div className="card table-list-card">
                <div className="card-body">
                  <div className="table-top">
                    <div className="search-set">
                      <div className="search-input">
                        <input
                          type="text"
                          placeholder="Search by product and supplier name"
                          className="form-control form-control-sm formsearch"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Link to="#" className="btn btn-searchset">
                          <i data-feather="search" className="feather-search" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table datanew">
                      <thead>
                        <tr>
                          <th style={{ width: "20%" }}>Product Name</th>
                          <th style={{ width: "20%" }}>Supplier Name</th>
                          <th style={{ width: "15%" }}>Purchase Amount</th>
                          <th style={{ width: "10%" }}>Purchase Qty</th>
                          <th style={{ width: "10%" }}>Paid</th>
                          <th style={{ width: "10%" }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.length > 0 ? (
                          renderPurchaseRows()
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center">
                              No data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {/* Payment Tab */}
            <div
              className="tab-pane fade"
              id="payment-report"
              role="tabpanel"
              aria-labelledby="payment-report-tab"
            >
              <div className="card table-list-card">
                <div className="card-body">
                  <div className="table-top">
                    <div className="search-set">
                      <div className="search-input">
                        <input
                          type="text"
                          placeholder="Search by product name, supplier name, reference, amount, paid"
                          className="form-control form-control-sm formsearch"
                        />
                        <Link to="#" className="btn btn-searchset">
                          <i data-feather="search" className="feather-search" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table datanew">
                      <thead>
                        <tr>
                          <th>Purchase Amount</th>
                          <th>Reference</th>
                          <th>Supplier Name</th>
                          <th>Amount</th>
                          <th>Paid</th>
                          <th>Paid By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierData.length > 0 ? (
                          renderPaymentRows()
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center">
                              No data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {/* Return Tab */}
            <div
              className="tab-pane fade"
              id="return-report"
              role="tabpanel"
              aria-labelledby="return-report-tab"
            >
              <div className="card table-list-card">
                <div className="card-body">
                  <div className="table-top">
                    <div className="search-set">
                      <div className="search-input">
                        <input
                          type="text"
                          placeholder="Search"
                          className="form-control form-control-sm formsearch"
                        />
                        <Link to="#" className="btn btn-searchset">
                          <i data-feather="search" className="feather-search" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table datanew">
                      <thead>
                        <tr>
                          <th>Reference</th>
                          <th>Supplier Name</th>
                          <th>Amount</th>
                          <th>Paid</th>
                          <th>Due Amount</th>
                          <th>Status</th>
                          <th>Payment Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierData.length > 0 ? (
                          renderReturnRows()
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center">
                              No data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierReport;
